import type {
  ApiExtractionMilestone,
  Book,
  OAuthChecklistItem,
  Phase3State,
  PlanningCard,
  PluginManifest,
  PluginRecord,
  PreflightCheck,
  PreflightResult,
  RevisionSnapshot,
} from "../types";

function nowIso(): string {
  return new Date().toISOString();
}

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value));
}

function totalWords(book: Book): number {
  return book.chapters.reduce((sum, chapter) => {
    const count = String(chapter.content || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean).length;
    return sum + count;
  }, 0);
}

export function ensureConnectorStates(value: Record<string, unknown> | undefined): {
  googleDrive: boolean;
  oneDrive: boolean;
  dropbox: boolean;
} {
  const current = value ?? {};
  return {
    googleDrive: Boolean(current.googleDrive),
    oneDrive: Boolean(current.oneDrive),
    dropbox: Boolean(current.dropbox),
  };
}

export function buildPlanningCards(book: Book): Array<{
  id: string;
  title: string;
  order: number;
  summary: string;
  status: string;
  tags: string[];
}> {
  return book.chapters.map((chapter, index) => ({
    id: chapter.id,
    title: chapter.title,
    order: index + 1,
    summary: chapter.tabData.summary || "",
    status: chapter.metadata.status || "draft",
    tags: chapter.metadata.tags || [],
  }));
}

export function createRevisionSnapshot(book: Book, note: string): RevisionSnapshot {
  const words = book.chapters.reduce((sum, chapter) => {
    const count = chapter.content.trim().split(/\s+/).filter(Boolean).length;
    return sum + count;
  }, 0);
  return {
    id: `snapshot-${Date.now()}`,
    createdAt: nowIso(),
    note: note.trim() || "Manual snapshot",
    wordCount: words,
    book: clone(book),
  };
}

export function restoreRevisionSnapshot(snapshot: RevisionSnapshot): Book {
  return clone(snapshot.book);
}

export const TIER_LIMITS = {
  free: 20000,
  pro: 120000,
  enterprise: 500000,
} as const;

export function normalizePhase3State(state: unknown): Phase3State {
  const current = (state as Partial<Phase3State>) ?? {};
  return {
    plugins: Array.isArray(current.plugins) ? current.plugins : [],
    tokenMetering: {
      tier: current.tokenMetering?.tier ?? "free",
      limit: current.tokenMetering?.limit ?? TIER_LIMITS.free,
      used: current.tokenMetering?.used ?? 0,
      history: Array.isArray(current.tokenMetering?.history) ? current.tokenMetering.history : [],
    },
    oauth: {
      enabled: Boolean(current.oauth?.enabled),
      linkedAccounts: Array.isArray(current.oauth?.linkedAccounts) ? current.oauth.linkedAccounts : [],
      migrationChecklist: Array.isArray(current.oauth?.migrationChecklist)
        ? current.oauth.migrationChecklist
        : [
            { id: "provider-inventory", label: "Inventory existing provider identities", done: false },
            { id: "linking-ui", label: "Account linking UX complete", done: false },
            { id: "token-compat", label: "Session/token compatibility validated", done: false },
            { id: "rollback", label: "Rollback path tested", done: false },
          ],
    },
    mcp: {
      approvedTools: Array.isArray(current.mcp?.approvedTools) ? current.mcp.approvedTools : [],
      callAudit: Array.isArray(current.mcp?.callAudit) ? current.mcp.callAudit : [],
    },
    apiExtraction: {
      milestones: Array.isArray(current.apiExtraction?.milestones)
        ? current.apiExtraction.milestones
        : [
            { id: "contracts", label: "Define shared API contracts", done: false },
            { id: "feedback-service", label: "Extract feedback ingestion service", done: false },
            { id: "publishing-service", label: "Extract publishing preflight service", done: false },
            { id: "ai-usage-service", label: "Extract AI usage/metering service", done: false },
          ],
    },
    advancedPublishing: {
      selectedTemplate: current.advancedPublishing?.selectedTemplate ?? "kdp-print-pro",
      lastChecklist: Array.isArray(current.advancedPublishing?.lastChecklist)
        ? current.advancedPublishing.lastChecklist
        : [],
    },
  };
}

export function registerPlugin(
  phase3State: Phase3State,
  manifest: PluginManifest,
  signature: string | null
): { ok: boolean; state: Phase3State; plugin?: PluginRecord; validation: { problems: string[] } } {
  const next = normalizePhase3State(phase3State);
  if (!manifest?.id || !manifest?.name || !manifest?.version) {
    return {
      ok: false,
      validation: { problems: ["Missing id/name/version"] },
      state: next,
    };
  }
  const plugin: PluginRecord = {
    id: manifest.id,
    name: manifest.name,
    version: manifest.version,
    author: manifest.author,
    entrypoints: manifest.entrypoints,
    compatibilityRange: manifest.compatibilityRange,
    permissions: Array.isArray(manifest.permissions) ? manifest.permissions : [],
    signed: Boolean(signature),
    signature: signature || null,
    installedAt: nowIso(),
    mode: signature ? "privileged-ready" : "sandbox-only",
  };
  next.plugins = next.plugins.filter((item) => item.id !== plugin.id);
  next.plugins.push(plugin);
  return { ok: true, state: next, plugin, validation: { problems: [] } };
}

export function consumeManagedTokens(
  phase3State: Phase3State,
  prompt: string,
  tier: keyof typeof TIER_LIMITS
): { state: Phase3State; result: { allowed: boolean; cost: number; used: number; limit: number } } {
  const next = normalizePhase3State(phase3State);
  const limit = TIER_LIMITS[tier];
  const cost = Math.max(1, Math.ceil(prompt.length / 4));
  const allowed = next.tokenMetering.used + cost <= limit;
  if (allowed) {
    next.tokenMetering.used += cost;
  }
  next.tokenMetering.tier = tier;
  next.tokenMetering.limit = limit;
  next.tokenMetering.history.push({ at: nowIso(), cost, allowed });
  return {
    state: next,
    result: {
      allowed,
      cost,
      used: next.tokenMetering.used,
      limit,
    },
  };
}

export function linkOAuthAccount(phase3State: Phase3State, provider: string, providerUserId: string): Phase3State {
  const next = normalizePhase3State(phase3State);
  next.oauth.enabled = true;
  next.oauth.linkedAccounts.push({
    provider,
    providerUserId,
    linkedAt: nowIso(),
  });
  return next;
}

export function toggleOAuthChecklistItem(phase3State: Phase3State, itemId: string): Phase3State {
  const next = normalizePhase3State(phase3State);
  next.oauth.migrationChecklist = next.oauth.migrationChecklist.map((item: OAuthChecklistItem) =>
    item.id === itemId ? { ...item, done: !item.done } : item
  );
  return next;
}

export function evaluateMcpToolCall(
  phase3State: Phase3State,
  toolName: string,
  scope: string
): { approved: boolean; state: Phase3State } {
  const next = normalizePhase3State(phase3State);
  const approvedScopes = new Set([
    "project.read",
    "project.write",
    "feedback.submit",
    "publishing.preflight",
    "plugins.read",
  ]);
  const approved = approvedScopes.has(scope);
  next.mcp.callAudit.push({
    at: nowIso(),
    toolName,
    scope,
    approved,
  });
  if (approved && !next.mcp.approvedTools.some((item) => item.toolName === toolName && item.scope === scope)) {
    next.mcp.approvedTools.push({ toolName, scope, approvedAt: nowIso() });
  }
  return { approved, state: next };
}

export function buildPublishingTemplateChecklist(templateId: string): Array<{ id: string; label: string; done: boolean }> {
  const templates: Record<string, string[]> = {
    "kdp-print-pro": [
      "Trim size matches KDP target profile",
      "Margins and gutter validated by page count",
      "Font embedding and print-safe image DPI validated",
      "Front/back matter checklist complete",
    ],
    "kdp-ebook-pro": [
      "EPUB nav and manifest checks pass",
      "Heading hierarchy validated",
      "Image reflow behavior reviewed",
      "Metadata and rights fields complete",
    ],
    "ingramspark-print-pro": [
      "IngramSpark trim/bleed rules validated",
      "Embedded fonts and print quality checks pass",
      "Cover/interior specs selected",
      "Distribution metadata complete",
    ],
  };
  const selected = templates[templateId] ?? templates["kdp-print-pro"];
  return selected.map((label, index) => ({
    id: `${templateId}-${index + 1}`,
    label,
    done: false,
  }));
}

export function setApiMilestoneDone(phase3State: Phase3State, milestoneId: string, done: boolean): Phase3State {
  const next = normalizePhase3State(phase3State);
  next.apiExtraction.milestones = next.apiExtraction.milestones.map((item: ApiExtractionMilestone) =>
    item.id === milestoneId ? { ...item, done } : item
  );
  return next;
}

export function runPublishingPreflight(
  book: Book,
  platform: "kdp" | "ingramspark",
  profile: "print" | "ebook"
): PreflightResult {
  const checks: PreflightCheck[] = [];
  const words = totalWords(book);
  checks.push({
    id: "content",
    label: "Manuscript has chapter content",
    pass: book.chapters.some((chapter) => chapter.content.trim().length > 0),
  });
  checks.push({
    id: "title",
    label: "Book title is set",
    pass: Boolean(book.title.trim()),
  });
  if (platform === "kdp") {
    checks.push({
      id: "kdp-word-count",
      label: "KDP manuscript baseline",
      pass: words >= 500,
    });
  } else {
    checks.push({
      id: "ingram-word-count",
      label: "IngramSpark manuscript baseline",
      pass: words >= 1000,
    });
  }
  checks.push({
    id: "profile",
    label: "Publishing profile selected",
    pass: profile === "print" || profile === "ebook",
  });
  return {
    platform,
    profile,
    checks,
    pass: checks.every((item) => item.pass),
    generatedAt: nowIso(),
  };
}
